import type { TextBox, TextChain } from '@/api/types/stowed-element'
import type { ShapeData } from '@/lib/shapes/types/shape-data'

/**
 * Shared by `toWireTextChains`/`fromWireTextChains`: rewrites every member's identifier via
 * `resolve`, dropping a member `resolve` cannot answer for and any chain left with fewer than 2 —
 * the same "chain must have 2+ resolvable members or it isn't a chain" rule `removeLinkAfter` above
 * already enforces.
 */
export function remapChainMemberIds(
  chains: TextChain[],
  resolve: (id: string) => string | undefined
): TextChain[] {
  const resolved: TextChain[] = []

  for (const chain of chains) {
    const textBox: TextBox[] = []

    for (const tb of chain.textBox) {
      const next = resolve(tb.uid)

      if (next != null) textBox.push({ uid: next })
    }

    if (textBox.length >= 2) resolved.push({ id: chain.id, layer: chain.layer, textBox })
  }

  return resolved
}

/**
 * Translates a chain list's `textBox[].uid` from the client-minted chain-membership counter
 * (`mintTextFrameUid`/`ensureFrameUid` in `pages/home`'s chain hooks) to each member's real `objectId`, for the wire
 * only. Inverse of `fromWireTextChains` — see that function for the read-side half of this pair.
 *
 * `uid` stays the internal chain-membership key everywhere else in this module and in `pages/home` —
 * `chainContaining`, `hasOutgoingLink`/`hasIncomingLink`, `canLink`, the seam memo, undo/redo — none
 * of that changes. This is the one seam where the payload leaving the app has to speak the server's
 * identifier (`objectId`) instead of the counter that only ever meant something inside this session.
 * Call it once, right before a `TextChain[]` is embedded in a `linkedTextElements` payload — never
 * earlier, or every uid-keyed lookup upstream of that point breaks.
 *
 * A member whose `uid` cannot be resolved against `shapes` (the shape was deleted mid-gesture, or a
 * uid was stamped before the corresponding `updateShape` call landed) is dropped rather than sent as
 * a reference the server cannot resolve either way.
 */
export function toWireTextChains(chains: TextChain[], shapes: readonly ShapeData[]): TextChain[] {
  const objectIdByUid = new Map<string, string>()

  for (const shape of shapes) {
    if (shape.uid != null) objectIdByUid.set(String(shape.uid), shape.id)
  }

  return remapChainMemberIds(chains, (uid) => objectIdByUid.get(uid))
}

/**
 * Inverse of `toWireTextChains`: translates a chain list just loaded from the server — whose
 * `textBox[].uid` now carries each member's `objectId` — back into this session's own uid-keyed
 * chain-membership scheme, so `chainContaining`/`canLink`/the seam memo/undo all keep working
 * against `TextChain.textBox[].uid` exactly as they always have.
 *
 * `shapes` must carry each element's own persisted `uid` (the field `readShapeTextFrameUidsFromCheckpointMetadata`
 * reads, via `checkpointToShapeData` — call this with that SAME parse of the checkpoint metadata,
 * not a live `ShapeManagerRef.getShapes()` snapshot, since this runs at load time before shapes are
 * necessarily in the manager yet). A member whose `objectId` cannot be resolved (or whose shape has
 * no `uid` of its own) is dropped, same as `toWireTextChains`.
 */
export function fromWireTextChains(chains: TextChain[], shapes: readonly ShapeData[]): TextChain[] {
  const uidByObjectId = new Map<string, string>()

  for (const shape of shapes) {
    if (shape.uid != null) uidByObjectId.set(shape.id, String(shape.uid))
  }

  return remapChainMemberIds(chains, (objectId) => uidByObjectId.get(objectId))
}
